import type { PipelineValidationIssue } from './validatePipeline';
import { TOOL_NAME_PATTERN } from '../../api/pipelineRefs';
import { isRecord } from '../../api/typeHelpers';

// Mirrors the structural half of Go validateAgentConfig
// (internal/pipeline/validation/agent.go). Whether a tool is offered and the
// contract is shipped are server checks: the save answers them.
const AGENT_CONTRACT_PATTERN = /^[a-z][a-z0-9_]*@[1-9][0-9]*$/;
// Go types.MaxAgent* (the global caps) and agentruntime.DefaultMaxTurns /
// DefaultMaxCostUSD (what a limit left empty gets). Change both together.
export const MAX_AGENT_TURNS = 60;
export const MAX_AGENT_COST_USD = 5;
export const MAX_AGENT_TIMEOUT_S = 1800;
export const MAX_AGENT_TOOL_CALLS = 40;
export const DEFAULT_AGENT_MAX_TURNS = 12;
export const DEFAULT_AGENT_MAX_COST_USD = 1;
// Go types.DefaultAgentTimeoutS (internal/pipeline/types/agent.go).
export const DEFAULT_AGENT_TIMEOUT_S = 900;

export function validateAgentCheckpoint(
  cp: Record<string, unknown>,
  label: string,
  checkpointId: string | null,
  issues: PipelineValidationIssue[]
): void {
  const error = (message: string) => issues.push({ level: 'error', checkpointId, message });
  if (!isRecord(cp.agent)) {
    error(`Checkpoint "${label}" is an agent checkpoint but has no agent block.`);
    return;
  }
  const agent = cp.agent;
  // Mirrors Go validateAgentInstructions: the instructions are a prompt template
  // or an inline objective (a saved director recipe), exactly one of them.
  const prompt = typeof agent.prompt_template_id === 'string' ? agent.prompt_template_id.trim() : '';
  const objective = typeof agent.objective === 'string' ? agent.objective.trim() : '';
  if (prompt && objective) {
    error(`Checkpoint "${label}": an agent has prompt_template_id or objective, not both.`);
  } else if (!prompt && !objective) {
    error(`Checkpoint "${label}": agent.prompt_template_id or agent.objective is required (the agent's instructions).`);
  }

  const tools = (Array.isArray(agent.tools) ? agent.tools : [])
    .filter((tool): tool is string => typeof tool === 'string')
    .map((tool) => tool.trim())
    .filter((tool) => tool !== '' && tool !== 'finish');
  if (tools.length === 0) error(`Checkpoint "${label}": agent.tools must list at least one tool besides finish.`);
  tools
    .filter((tool) => !TOOL_NAME_PATTERN.test(tool))
    .forEach((tool) => error(`Checkpoint "${label}": agent tool "${tool}" is not a valid tool name.`));

  // Mirrors Go validateAgentConfig (internal/pipeline/validation/agent.go:72-74):
  // dots become underscores for the model (agentruntime.WireName), so two
  // tools that differ only that way reach the model under the same name.
  const wireName = (name: string): string => name.replace(/\./g, '_');
  const wireNames = new Map<string, string>([[wireName('finish'), 'finish']]);
  tools.forEach((tool) => {
    const wire = wireName(tool);
    const other = wireNames.get(wire);
    if (other !== undefined && other !== tool) {
      error(`Checkpoint "${label}": agent tools "${other}" and "${tool}" reach the model under the same name "${wire}".`);
    } else {
      wireNames.set(wire, tool);
    }
  });

  // Mirrors Go validateAgentResultShape: exactly one of a shipped contract and
  // an inline output_schema. The inline schema's own rules are the server's.
  const contract = typeof agent.output_contract === 'string' ? agent.output_contract.trim() : '';
  const schema = agent.output_schema;
  if (schema !== undefined && schema !== null) {
    if (contract) {
      error(`Checkpoint "${label}": set agent.output_contract or agent.output_schema, not both.`);
    } else if (!isRecord(schema) || schema.type !== 'object') {
      error(`Checkpoint "${label}": agent.output_schema must be a JSON Schema object ({"type": "object", "properties": {...}}).`);
    }
  } else if (!AGENT_CONTRACT_PATTERN.test(contract)) {
    error(`Checkpoint "${label}": agent.output_contract must look like <name>@<version>.`);
  }

  // Mirrors Go validateAgentImageGenerator (internal/pipeline/validation/agent_image.go):
  // an image model override names provider and model together (settings alone
  // keep the pipeline's image model). Only read when the agent may generate;
  // whether the model is one of the provider's image models is the save's
  // registry check.
  if (tools.includes('image.generate') && isRecord(agent.image)) {
    const provider = typeof agent.image.provider === 'string' ? agent.image.provider.trim() : '';
    const model = typeof agent.image.model === 'string' ? agent.image.model.trim() : '';
    if (Boolean(provider) !== Boolean(model)) {
      error(
        `Checkpoint "${label}": agent.image.provider and agent.image.model go together: set both, or neither to use the pipeline's image model.`
      );
    }
  }

  const limits = isRecord(agent.limits) ? agent.limits : {};
  const over = (value: unknown, max: number) => typeof value === 'number' && (value < 0 || value > max);
  // Wording mirrors Go validateAgentLimits (internal/pipeline/validation/agent.go:109-118)
  // exactly, including the "(0 = default N)" clause and two-decimal USD figures —
  // this is the first place a user sees this text live, next to the field.
  if (over(limits.max_turns, MAX_AGENT_TURNS)) {
    error(
      `Checkpoint "${label}": agent.limits.max_turns must be between 1 and ${MAX_AGENT_TURNS} (0 = default ${DEFAULT_AGENT_MAX_TURNS}).`
    );
  }
  if (over(limits.max_cost_usd, MAX_AGENT_COST_USD)) {
    error(
      `Checkpoint "${label}": agent.limits.max_cost_usd must be at most ${MAX_AGENT_COST_USD.toFixed(2)} (0 = default ${DEFAULT_AGENT_MAX_COST_USD.toFixed(2)}).`
    );
  }
  // The model-only cap shares the total's ceiling; 0 or unset = no model-only cap.
  if (over(limits.max_model_cost_usd, MAX_AGENT_COST_USD)) {
    error(
      `Checkpoint "${label}": agent.limits.max_model_cost_usd must be at most ${MAX_AGENT_COST_USD.toFixed(2)} (0 = no model-only cap).`
    );
  }
  if (over(limits.timeout_s, MAX_AGENT_TIMEOUT_S)) {
    error(
      `Checkpoint "${label}": agent.limits.timeout_s must be at most ${MAX_AGENT_TIMEOUT_S} (0 = default ${DEFAULT_AGENT_TIMEOUT_S}).`
    );
  }
  const perTool = isRecord(limits.per_tool) ? limits.per_tool : {};
  Object.entries(perTool).forEach(([tool, cap]) => {
    if (!tools.includes(tool)) error(`Checkpoint "${label}": agent.limits.per_tool names "${tool}", which is not in agent.tools.`);
    else if (typeof cap !== 'number' || cap < 1 || cap > MAX_AGENT_TOOL_CALLS) {
      error(`Checkpoint "${label}": agent.limits.per_tool[${tool}] must be between 1 and ${MAX_AGENT_TOOL_CALLS}.`);
    }
  });
}
